import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { MetadataService } from 'src/app/common/services/metadata.service';
import { BaseDynamicViewComponent } from '../base-dynamic-form/base-dynamic-view.component';
import { DynamicInputType } from '../../../dynamic-form-v2/enums/DynamicInputType';
import { DynamicViewType } from '../../enums/DynamicViewType';
import { DynamicCardType } from '../../../dynamic-form-v2/enums/DynamicCardType';
import { BaseViewFieldType } from '../../enums/BaseViewFieldType';
import { DialogAction } from '../../../base-dialog/models';
import { SettingsService } from 'src/app/entities/settings/services/settings.service';

@Component({
  selector: 'app-metadata-view',
  templateUrl: './metadata-view.component.html',
  styleUrls: ['./metadata-view.component.sass'],
  providers: [ConfirmationService],
})
export class MetadataViewComponent extends BaseDynamicViewComponent implements OnInit {
  id: string;
  record: any;
  
  relocalize = false;
  disabledLocalizeButton = false;
  
  get inputType(): typeof DynamicInputType {
    return DynamicInputType;
  }
  get ViewType(): typeof DynamicViewType {
    return DynamicViewType;
  }
  get cardType(): typeof DynamicCardType {
    return DynamicCardType;
  }

  constructor(
    private metadataService: MetadataService,
    private messageService: MessageService,
    private settingsService: SettingsService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    super();
  }

  ngOnInit(): void {
    this.id = this.route.snapshot.params['id'];

    this.fields = [
      {
        title: 'Main data',
        fields: [
          {
            name: "Model Label",
            inputType: DynamicViewType.BaseViewField,
            viewTypes: [BaseViewFieldType.Text],
            clearField: true,
            key: 'modelLabel',
            isRequired: true,
            label: 'Model Label',
            columnWidth: 6
          },
          {
            name: "Model Name",
            inputType: DynamicViewType.BaseViewField,
            viewTypes: [BaseViewFieldType.Text],
            clearField: true,
            key: 'modelName',
            isRequired: true,
            label: 'Model Name',
            columnWidth: 6
          },
        ],
        type: DynamicCardType.Card
      },
      {
        title: 'Field Types',
        fields: [
          {
            name: "Fields", 
            inputType: DynamicViewType.TableView,
            key: 'fields',
            label: 'Fields',
            options: {
              fieldName: '',
              columnDefs: [
                {
                  headerName: 'Name',
                  field: 'name',
                  sortable: true,
                  unSortIcon: true,
                  resizable: true,
                  filter: 'agTextColumnFilter',
                  floatingFilter: true,
                },
                {
                  headerName: 'Label',
                  field: 'label',
                  sortable: true,
                  unSortIcon: true,
                  resizable: true,
                  filter: 'agTextColumnFilter',
                  floatingFilter: true,
                },
                {
                  headerName: 'Input Widget',
                  field: 'inputWidget',
                  sortable: true,
                  unSortIcon: true,
                  resizable: true,
                  filter: 'agTextColumnFilter',
                  floatingFilter: true,
                },
                {
                  headerName: 'Data Type',
                  field: 'type',
                  sortable: true,
                  unSortIcon: true,
                  resizable: true,
                  filter: 'agTextColumnFilter',
                  floatingFilter: true,
                },
                {
                  headerName: 'Position',
                  field: 'position',
                  sortable: true,
                  unSortIcon: true,
                  resizable: true,
                },
                {
                  headerName: 'Column Width',
                  field: 'columnWidth',
                  sortable: true,
                  unSortIcon: true,
                  resizable: true,
                },
                {
                  headerName: 'Required',
                  field: 'required',
                  cellRenderer: (params: any) => params.value ? 'Yes' : 'No',
                  sortable: true,
                  unSortIcon: true,
                  resizable: true,
                  filter: 'agSetColumnFilter',
                  filterParams: {
                    values: [true, false],
                    valueFormatter: (params: any) => params.value ? 'Yes' : 'No'
                  },
                  floatingFilter: true,
                },
                {
                  headerName: 'Export to Gamedata',
                  field: 'gamedataExportEnabled',
                  cellRenderer: (params: any) => params.value ? 'Yes' : 'No',
                  sortable: true,
                  unSortIcon: true,
                  resizable: true,
                  filter: 'agSetColumnFilter',
                  filterParams: {
                    values: [true, false],
                    valueFormatter: (params: any) => params.value ? 'Yes' : 'No'
                  },
                  floatingFilter: true,
                },
                {
                  headerName: 'Localizable',
                  field: 'localize',
                  cellRenderer: (params: any) => params.value ? 'Yes' : 'No',
                  sortable: true,
                  unSortIcon: true,
                  resizable: true,
                  filter: 'agSetColumnFilter',
                  filterParams: {
                    values: [true, false],
                    valueFormatter: (params: any) => params.value ? 'Yes' : 'No'
                  },
                  floatingFilter: true,
                },
              ],
            }
          },
        ],
        type: DynamicCardType.Card
      }
    ];

    this.loadMetadata();

    this.title = "Metadata";
    this.isLoading = false;
  }

  private loadMetadata() {
    if (!this.id) {
      return;
    }

    this.isLoading = true;

    this.metadataService.getMetadataById(this.id).subscribe({
      next: (metadata) => {
        this.record = metadata;

        this.isLoading = false;
        this.relocalize = !!metadata.localizationConfig;
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load metadata'
        });
        console.error('Error loading metadata:', error);

        this.isLoading = false;
      }
    });
  }

  navigateToEdit() {
    this.router.navigate(['/dynamic-entities/edit', this.record.id]);
  }

  shouldDisplayField(field: any, record: any): boolean {
    // If field has displayControls and displayType, check them
    if (field.displayControls && field.displayType) {
      // Get the value that controls this field's visibility
      const controlValue = record[field.displayType];
      
      // Check each display control
      for (const [key, control] of Object.entries(field.displayControls)) {
        if (typeof control === 'function') {
          // If any control returns false, hide the field
          if (!control(controlValue, key)) {
            return false;
          }
        }
      }
    }
    
    // If no display controls or all controls pass, show the field
    return true;
  }
}
